import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import GroupDonationInitiatorModal from "../components/GroupDonationInitiatorModal";
import DonationButton from "../components/DonationButton";
import { FRONT_URL, MODAL_ID } from "../constants";
import { ANIMAL_SPLIT_CORE } from "../constants";
import { toggleModal } from "../utils/utils";
import AnimalEasterEggBadge from "../components/AnimalEasterEggBadge";
import { uploadImage } from "../services/AnimalSplitCore";
import WeChatShareHelpModal from "../components/WeChatShareHelpModal";
import { isWeChatBrowser } from "../utils/browser";
import {
  buildSettlementGroups,
  renderSettlementsShareCanvas,
  shareCanvasImage,
} from "../utils/settlementShareImage";
import "./ModalTheme.css";

export default function ShareGroup(props) {
  const { t } = useTranslation();
  const {
    modalId,
    groupId,
    groupName,
    contributors,
    isSettled = false,
    settlements,
    settlementType,
    contributorIdDict,
    sceneVariant,
  } = props;

  const handleOnClick = (e) => {
    e.target.select();
  };

  const [copied, setCopied] = useState(false);
  const baseUrl = FRONT_URL[process.env.NODE_ENV].URL;
  const shareUrl = `${baseUrl.replace(/\/$/, "")}/group/${groupId}`;
  const [sharingSettlements, setSharingSettlements] = useState(false);
  const [isCreatingDonation, setIsCreatingDonation] = useState(false);
  const groupDonateModalId = `${modalId}-group-donate-modal`;
  const weChatShareHelpModalId = `${modalId}-wechat-share-help-modal`;

  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const openWeChatShareHelp = () => {
    try {
      const currentEl = document.getElementById(modalId);
      const nextEl = document.getElementById(weChatShareHelpModalId);
      if (!currentEl || !nextEl || !window?.bootstrap?.Modal) return;

      const currentInst =
        window.bootstrap.Modal.getInstance(currentEl) || new window.bootstrap.Modal(currentEl);

      const showNext = () => {
        currentEl.removeEventListener("hidden.bs.modal", showNext);
        const nextInst =
          window.bootstrap.Modal.getInstance(nextEl) || new window.bootstrap.Modal(nextEl);
        nextInst.show();
      };

      currentEl.addEventListener("hidden.bs.modal", showNext);
      currentInst.hide();
    } catch (e) {
      // fallback: try best-effort toggle
      toggleModal(weChatShareHelpModalId);
    }
  };

  useEffect(() => {
    const modalEl = document.getElementById(modalId);
    if (!modalEl) return;

    const handleShow = (e) => {
      if (!groupId) {
        // show validation and prevent opening empty share modal
        toggleModal(MODAL_ID.VALIDATION_ERROR, {
          title: "Missing event",
          message: "No event ID available to share.",
        });
        const bsModal = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
        bsModal.hide();
      }
    };

    modalEl.addEventListener("show.bs.modal", handleShow);
    return () => modalEl.removeEventListener("show.bs.modal", handleShow);
  }, [modalId, groupId]);

  const copyShareUrlToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("copy failed", err);
    }
  };

  const handleCopy = async () => {
    if (isWeChatBrowser()) {
      openWeChatShareHelp();
      return;
    }
    await copyShareUrlToClipboard();
  };

  const handleShareWebsite = async () => {
    if (isWeChatBrowser()) {
      openWeChatShareHelp();
      return;
    }
    const title = groupName?.trim() || t("event.share_event") || "Share Event";
    const text = groupName?.trim() || "";

    // Try native share first
    if (canNativeShare) {
      try {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall through to copy
        if (err.name === "AbortError") return; // User cancelled, do nothing
      }
    }

    // Fallback: copy to clipboard
    try {
      await copyShareUrlToClipboard();
    } catch (err) {
      console.error("share/copy failed", err);
    }
  };

  const settlementGroups = buildSettlementGroups({
    contributors,
    settlements,
    settlementType,
    contributorIdDict,
  });

  const canvasToPngBlob = (canvas) =>
    new Promise((resolve, reject) => {
      if (!canvas?.toBlob) return reject(new Error("canvas.toBlob not supported"));
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Failed to encode image"));
        if (!blob.type) return resolve(new Blob([blob], { type: "image/png" }));
        resolve(blob);
      }, "image/png");
    });

  const handleShareSettlements = async () => {
    if (!groupId) return;
    if (sharingSettlements) return;

    if (!settlementGroups || settlementGroups.length === 0) {
      toggleModal(MODAL_ID.VALIDATION_ERROR, {
        title: t("general.error") || "Oops",
        message: t("general.no_settlements_to_share") || "No settlements to share yet.",
      });
      return;
    }

    try {
      setSharingSettlements(true);

      const widthCssPx = Math.min(window.innerWidth || 390, 520);
      const logoSrc = `${process.env.PUBLIC_URL || ""}/images/logo/settlement.png`;
      const canvas = await renderSettlementsShareCanvas({
        groupName,
        groupId,
        settlementGroups,
        widthCssPx,
        logoSrc,
        owesLabel: t("general.owes") || "owes",
        sceneVariant,
      });

      const filename = `settlements-${groupId}.png`;
      const title = groupName?.trim()
        ? `${groupName.trim()} — Settlements`
        : (t("group.settlements") || "Settlements");
      const text = t("group.settlements") || "Settlements";

      // Upload to backend to get a stable image URL (WeChat-friendly)
      let imageUrl = "";
      let imageFile = null;
      try {
        const blob = await canvasToPngBlob(canvas);
        const file = new File([blob], filename, { type: "image/png", lastModified: Date.now() });
        imageFile = file;
        const formData = new FormData();
        formData.append("image", file);
        const resp = await uploadImage(formData); // { status, payload: { id, proxyUrl, key } }
        const proxyPath = resp?.payload?.proxyUrl || "";
        const apiBase = String(ANIMAL_SPLIT_CORE[process.env.NODE_ENV].URL || "").replace(/\/$/, "");
        if (proxyPath && apiBase) imageUrl = `${apiBase}${proxyPath}`;
      } catch (e) {
        console.warn("upload image failed; falling back to file share", e);
      }

      // Immediately open the native share sheet (iOS can "Save Image" to Photos here).
      if (canNativeShare && imageFile) {
        try {
          const canShareFile = navigator.canShare ? navigator.canShare({ files: [imageFile] }) : true;
          if (canShareFile) {
            const payload = imageUrl ? { title, text, url: imageUrl, files: [imageFile] } : { title, text, files: [imageFile] };
            await navigator.share(payload);
            return;
          }
        } catch (e) {
          // fall through
        }
      }

      // If native share isn't available (e.g. WeChat webview), copy/open the URL.
      if (imageUrl && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(imageUrl);
          toggleModal(MODAL_ID.VALIDATION_ERROR, {
            title: t("general.copied") || "Copied",
            message: t("general.link_copied_paste_wechat") || "Image link copied. Paste it into WeChat to share.",
          });
          return;
        } catch (e) {
          // fall through
        }
      }

      if (imageUrl) {
        try {
          window.open(imageUrl, "_blank", "noopener,noreferrer");
          return;
        } catch (e) {
          // fall through
        }
      }

      // Final fallback: share/download directly from canvas
      await shareCanvasImage({ canvas, filename, title, text });
    } catch (e) {
      console.error("share settlements failed", e);
      toggleModal(MODAL_ID.VALIDATION_ERROR, {
        title: t("general.error") || "Oops",
        message: t("general.failed_to_share") || "Failed to share.",
      });
    } finally {
      setSharingSettlements(false);
    }
  };

  const handleOpenGroupDonate = () => {
    try {
      const currentEl = document.getElementById(modalId);
      const nextEl = document.getElementById(groupDonateModalId);
      if (!currentEl || !nextEl || !window?.bootstrap?.Modal) return;

      const currentInst =
        window.bootstrap.Modal.getInstance(currentEl) || new window.bootstrap.Modal(currentEl);

      const showNext = () => {
        currentEl.removeEventListener("hidden.bs.modal", showNext);
        const nextInst =
          window.bootstrap.Modal.getInstance(nextEl) || new window.bootstrap.Modal(nextEl);
        nextInst.show();
      };

      currentEl.addEventListener("hidden.bs.modal", showNext);
      currentInst.hide();
    } catch (e) {
      // fallback: do nothing
    }
  };

  const modal = (
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-modal as-theme">
          <div className="modal-header">
            <div className="as-modal-header-row">
              <div className="as-modal-title-row">
                <span className="as-modal-icon-badge" aria-hidden="true">
                  🔗
                </span>
                <h5 className="modal-title" id="exampleModalLabel">
                  {t("event.share_event")}
                </h5>
              </div>
              <div className="as-modal-actions">
                <AnimalEasterEggBadge seed={`${groupId}:share-modal`} />
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
            </div>
          </div>

          <div className={"modal-body"}>
            <div className="mb-3">
              <label className="form-label">{t("event.event_url")}</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={shareUrl}
                  readOnly
                  onClick={handleOnClick}
                  spellCheck={false}
                />
                <button
                  type="button"
                  className={`btn as-btn-primary-gradient`}
                  onClick={handleCopy}
                >
                  {copied ? (t("general.copied") || "Copied") : (t("event.copy_uri") || "Copy")}
                </button>
              </div>
            </div>

            <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm as-btn-primary-gradient d-inline-flex align-items-center"
                onClick={handleShareWebsite}
              >
                <i className="bi bi-share me-2" aria-hidden="true" />
                {t("general.share") || "Share"}
              </button>
              {isSettled && (
                <button
                  type="button"
                  className="btn btn-sm as-btn-primary-gradient d-inline-flex align-items-center"
                  onClick={handleShareSettlements}
                  disabled={sharingSettlements}
                >
                  <span className="me-2" aria-hidden="true">
                    🐾
                  </span>
                  <i className="bi bi-receipt-cutoff me-2" aria-hidden="true" />
                  {sharingSettlements
                    ? (t("general.generating") || "Generating…")
                    : (t("group.share_settlements") || "Share settlements")}
                </button>
              )}
            </div>

          </div>

          <div className="modal-footer as-modal-footer">
            <DonationButton className="btn btn-outline-primary me-2" />
            {!isSettled && (
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={handleOpenGroupDonate}
              disabled={isCreatingDonation}
            >
              <span className="me-2" aria-hidden="true">💝</span>
              {t("general.donate_as_group") || "Donate as a group"}
            </button>
            )}
            <button
              type="button"
              className="btn as-btn-soft"
              data-bs-dismiss="modal"
            >
              {t("general.close")}
            </button>
          </div>

        </div>
      </div>
    </div>
  );

  const groupDonateModal = (
    <GroupDonationInitiatorModal
      modalId={groupDonateModalId}
      groupId={groupId}
      groupName={groupName}
      contributors={contributors}
      donationType="group_donate"
      isCreatingDonation={isCreatingDonation}
      setIsCreatingDonation={setIsCreatingDonation}
    />
  );

  const weChatShareHelpModal = (
    <WeChatShareHelpModal
      modalId={weChatShareHelpModalId}
      onCopyLink={copyShareUrlToClipboard}
      copied={copied}
    />
  );

  return (
    <>
      {modal}
      {groupDonateModal}
      {weChatShareHelpModal}
    </>
  );
}
